import { memo, useState, useRef, useEffect } from 'react'

import { Text } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type NavigationNotificationCount } from '@webapp/stores/notificationCount'
import { NavigationLink } from './components/NavigationLink'
import { usePromoProximity } from './hooks/usePromoProximity'
import messages from './messages'
import { NavigationSectionStyled } from './styles/NavigationSectionStyled'
import { NavigationStyled } from './styles/NavigationStyled'
import { PromoTintGlobalStyle } from './styles/promoTintStyles'
import { type INavigationItem } from './types'

export interface INavigationItems {
  dashboard: INavigationItem
  approve: INavigationItem
  expense: INavigationItem[]
  purchase: INavigationItem[]
  analytics: INavigationItem[]

  spend: INavigationItem[]
  settings: INavigationItem
  discoverMore?: INavigationItem
}

export interface INavigationProps {
  location?: string
  notificationCount?: NavigationNotificationCount
  navigationItems: INavigationItems
}

export const Navigation = memo(
  ({
    location = '/',
    notificationCount = {},
    navigationItems,
  }: INavigationProps) => {
    const intl = useProcurifyIntl()
    const [openedLink, setOpenedLink] = useState<INavigationItem['id']>(null)
    const [openedLinkCollapsed, setOpenedLinkCollapsed] =
      useState<INavigationItem['id']>(null)

    const wrapperRef = useRef(null)
    usePromoProximity(wrapperRef)

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpenedLinkCollapsed(null)
      }
    }

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [wrapperRef])

    const hasNavigationSubs = (navItem: INavigationItem) =>
      navItem.children &&
      Array.isArray(navItem.children) &&
      navItem.children.length > 0

    const renderNavigationLink = (navItem: INavigationItem) => {
      if (!navItem) return null

      return (
        <NavigationLink
          location={location}
          notificationKey={navItem.notificationKey}
          icon={navItem.icon}
          tag={navItem?.tag}
          isPromo={navItem.isPromo}
          title={navItem.title}
          to={navItem.url}
          legacy={navItem.legacy}
          navigationSubs={navItem.children}
          id={navItem.id}
          key={navItem.id}
          matchUrls={navItem?.matchUrls}
          exactMatch={navItem?.exactMatch}
          notificationCount={notificationCount}
          isOpen={openedLink === navItem.id}
          onOpen={(navItemId) => {
            if (!hasNavigationSubs(navItem)) return
            if (openedLink === navItemId) {
              setOpenedLink(null)
            } else {
              setOpenedLink(navItemId)
            }
          }}
          isCollapsedOpen={openedLinkCollapsed === navItem.id}
          onCollapsedOpen={(navItemId) => {
            if (!hasNavigationSubs(navItem)) return
            if (openedLink !== navItemId) {
              setOpenedLinkCollapsed(navItemId)
            }
          }}
          onLinkClick={() => setOpenedLinkCollapsed(null)}
        />
      )
    }

    const closeSublinkMenuOnEsc = (e) => {
      if (e.key === 'Escape') {
        setOpenedLink(null)
        setOpenedLinkCollapsed(null)
      }
    }

    return (
      <NavigationStyled
        className='qa-navigation'
        ref={wrapperRef}
        onKeyDown={closeSublinkMenuOnEsc}
      >
        <PromoTintGlobalStyle />
        {renderNavigationLink(navigationItems.dashboard)}

        {!!navigationItems.approve && (
          <NavigationSectionStyled>
            {renderNavigationLink(navigationItems.approve)}
          </NavigationSectionStyled>
        )}
        {!!navigationItems.expense?.length && (
          <NavigationSectionStyled>
            <Text bold gutter>
              {intl.formatMessage(messages.expenseSectionHeader)}
            </Text>
            {navigationItems.expense.map((nav) => renderNavigationLink(nav))}
          </NavigationSectionStyled>
        )}
        {!!navigationItems.purchase?.length && (
          <NavigationSectionStyled>
            <Text bold gutter>
              {intl.formatMessage(messages.purchaseSectionHeader)}
            </Text>
            {navigationItems.purchase.map((nav) => renderNavigationLink(nav))}
          </NavigationSectionStyled>
        )}
        {!!navigationItems.spend?.length && (
          <NavigationSectionStyled>
            <Text bold gutter>
              {intl.formatMessage(messages.spendSectionHeader)}
            </Text>
            {navigationItems.spend.map((nav) => renderNavigationLink(nav))}
          </NavigationSectionStyled>
        )}
        {!!navigationItems.analytics?.length && (
          <NavigationSectionStyled>
            <Text bold gutter>
              {intl.formatMessage(messages.analyticsSectionHeader)}
            </Text>
            {navigationItems.analytics.map((nav) => renderNavigationLink(nav))}
          </NavigationSectionStyled>
        )}

        {!!navigationItems.settings && (
          <NavigationSectionStyled>
            {renderNavigationLink(navigationItems.settings)}
          </NavigationSectionStyled>
        )}

        {!!navigationItems.discoverMore && (
          <NavigationSectionStyled>
            {renderNavigationLink(navigationItems.discoverMore)}
          </NavigationSectionStyled>
        )}
      </NavigationStyled>
    )
  }
)

Navigation.displayName = 'Navigation'
